import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Plus, Copy, Trash2, Save, X, AlertTriangle, Eye, Clock,
} from "lucide-react";

// =============================================================================
// AutoGenRulesAdmin — Phase 2-C
// =============================================================================
// Tenant admins can view, edit, duplicate and create rule groups that power
// the Zeiterfassung Auto-Generator. Each carrier (UPS, GLS, DPD, ...) has its
// own rule group with weekday-specific timing rules, pause logic, KM ranges,
// hard caps and overtime distribution settings.
// =============================================================================

const WEEKDAYS = [
  { key: "mon", label: "Montag" },
  { key: "tue", label: "Dienstag" },
  { key: "wed", label: "Mittwoch" },
  { key: "thu", label: "Donnerstag" },
  { key: "fri", label: "Freitag" },
  { key: "sat", label: "Samstag" },
  { key: "sun", label: "Sonntag" },
];

const EXCLUDE_WD_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function emptyWeekdayRule() {
  return {
    start: ["06:00", "07:00"],
    duration_min: 480,
    duration_max: 540,
    end: null,
  };
}

const EMPTY_RULE = {
  code: "",
  name: "",
  description: "",
  active: true,
  weekday_rules: {
    mon: emptyWeekdayRule(),
    tue: emptyWeekdayRule(),
    wed: emptyWeekdayRule(),
    thu: emptyWeekdayRule(),
    fri: emptyWeekdayRule(),
  },
  pause_so: {
    offset_min_after_start: [15, 20],
    offset_min_after_prev: [5, 10],
    so2_duration: [115, 130],
    so3_before_end: [15, 20],
  },
  pause_rp: {
    window: ["10:00", "12:00"],
    duration: [32, 36],
    extended_duration: 45,
  },
  km: { daily_range: [70, 90], fallback_start: 100000 },
  hard_caps: { latest_end: "18:30", max_span_min: 750 },
  overtime: { step: 15, max_per_day: 120, rp_extend_threshold: 60 },
  exclude_weekdays: [5, 6],
};

export default function AutoGenRulesAdmin() {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/auto-gen/rule-groups");
      setGroups(data?.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startEdit(g) { setEditing(JSON.parse(JSON.stringify(g))); }
  function startNew() {
    setEditing({ ...JSON.parse(JSON.stringify(EMPTY_RULE)), id: "", code: "" });
  }
  function startDuplicate(g) {
    const copy = JSON.parse(JSON.stringify(g));
    delete copy.id;
    copy.code = `${g.code}_KOPIE`;
    copy.name = `${g.name} (Kopie)`;
    setEditing(copy);
  }

  async function save() {
    if (!editing.code?.trim() || !editing.name?.trim()) {
      toast.error("Code und Name sind Pflicht");
      return;
    }
    try {
      const payload = { ...editing };
      if (!payload.id) delete payload.id;  // backend assigns uuid
      const { data } = await api.post("/auto-gen/rule-groups", payload);
      toast.success("Regel-Gruppe gespeichert");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  }

  async function remove(g) {
    if (!window.confirm(`Regel-Gruppe "${g.name}" wirklich löschen?`)) return;
    try {
      // Soft-delete by setting active=false (no delete endpoint exists; soft toggle keeps audit trail)
      await api.post("/auto-gen/rule-groups", { ...g, active: false });
      toast.success("Regel deaktiviert");
      await load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  }

  async function seedPresets() {
    try {
      const { data } = await api.post("/auto-gen/seed-presets");
      toast.success(`${data.inserted} Presets geseedet`);
      await load();
    } catch (e) {
      toast.error("Seeding fehlgeschlagen");
    }
  }

  return (
    <div data-testid="autogen-rules-admin" className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5">
          <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-display font-semibold text-zinc-50">Auto-Generator Regel-Gruppen</h3>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
              Konfiguriere Wochentag-Regeln, Pausen, Hard-Caps, KM-Range und Überstunden-Caps für jede
              Carrier-Regel. Diese Werte steuern direkt die Zeiterfassung-Auto-Generierung in Operations.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {groups.length === 0 && (
            <button onClick={seedPresets} data-testid="autogen-seed-btn"
              className="px-3 py-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium">
              Standard-Presets anlegen
            </button>
          )}
          <button onClick={startNew} data-testid="autogen-rule-new"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Plus className="w-3.5 h-3.5" /> Neue Regel-Gruppe
          </button>
        </div>
      </div>

      {/* Rules list */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Lade …</div>
        ) : groups.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Noch keine Regel-Gruppen. Klick auf <span className="text-emerald-400 font-medium">„Standard-Presets anlegen"</span> für UPS/GLS/DPD/CUSTOM.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Hard-Cap</th>
                <th className="px-4 py-3 text-left">KM-Range</th>
                <th className="px-4 py-3 text-left">Wochenende</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.id} data-testid={`autogen-rule-row-${g.code}`}
                  className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 font-mono text-xs text-amber-300">{g.code}</td>
                  <td className="px-4 py-2.5 text-zinc-100">{g.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-300">{g.hard_caps?.latest_end || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-300">
                    {g.km?.daily_range?.[0]}–{g.km?.daily_range?.[1]} km
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-400">
                    {(g.exclude_weekdays || []).map(i => EXCLUDE_WD_LABELS[i]).join("/")}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => startEdit(g)} data-testid={`autogen-rule-edit-${g.code}`}
                      className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                    <button onClick={() => startDuplicate(g)} data-testid={`autogen-rule-dup-${g.code}`}
                      className="text-xs text-zinc-400 hover:text-zinc-200 mr-3">
                      <Copy className="w-3 h-3 inline mr-0.5" /> Duplizieren
                    </button>
                    {g.code !== "CUSTOM" && (
                      <button onClick={() => remove(g)} data-testid={`autogen-rule-del-${g.code}`}
                        className="text-xs text-red-400 hover:text-red-300">
                        <Trash2 className="w-3 h-3 inline mr-0.5" /> Deaktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <RuleEditor rule={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

// ============================================================================
// RuleEditor — full-screen modal
// ============================================================================
function RuleEditor({ rule, onChange, onCancel, onSave }) {
  const [section, setSection] = useState("weekdays");

  function update(path, value) {
    const next = JSON.parse(JSON.stringify(rule));
    const keys = path.split(".");
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys[keys.length - 1]] = value;
    onChange(next);
  }
  function toggleWeekday(idx) {
    const cur = new Set(rule.exclude_weekdays || []);
    if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
    update("exclude_weekdays", [...cur].sort());
  }
  function setActiveWeekday(key, active) {
    const next = JSON.parse(JSON.stringify(rule));
    if (active) {
      if (!next.weekday_rules[key]) next.weekday_rules[key] = emptyWeekdayRule();
    } else {
      delete next.weekday_rules[key];
    }
    onChange(next);
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="autogen-rule-editor">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              {rule.id ? "Regel bearbeiten" : "Neue Regel-Gruppe"}
            </div>
            <h3 className="font-display font-semibold text-zinc-50 mt-0.5">
              {rule.name || "(Unbenannt)"}
            </h3>
          </div>
          <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-zinc-800 flex gap-1 flex-shrink-0">
          {[
            ["weekdays", "Wochentag-Regeln"],
            ["pauses", "Pausen"],
            ["km", "KM"],
            ["caps", "Hard-Caps"],
            ["overtime", "Überstunden"],
            ["preview", "Vorschau"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setSection(k)}
              data-testid={`autogen-rule-tab-${k}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${section === k ? "text-zinc-50 border-blue-500" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Top metadata fields are shared across tabs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-zinc-950 border border-zinc-800 rounded p-3">
            <Field label="Code (eindeutig)">
              <input value={rule.code} onChange={e => update("code", e.target.value.toUpperCase())}
                data-testid="autogen-rule-code"
                placeholder="UPS, GLS, DPD, CUSTOM_AMAZON…"
                className={INP + " font-mono uppercase"} />
            </Field>
            <Field label="Name (Anzeige)">
              <input value={rule.name} onChange={e => update("name", e.target.value)}
                data-testid="autogen-rule-name"
                className={INP} />
            </Field>
            <Field label="Aktiv">
              <label className="inline-flex items-center gap-2 text-sm text-zinc-200 cursor-pointer mt-1.5">
                <input type="checkbox" checked={!!rule.active} onChange={e => update("active", e.target.checked)}
                  className="w-4 h-4 rounded accent-emerald-500" />
                Regel ist aktiv
              </label>
            </Field>
            <Field label="Beschreibung" colSpan={3}>
              <input value={rule.description || ""} onChange={e => update("description", e.target.value)}
                placeholder="Kurzbeschreibung der Schicht-Logik dieser Regel"
                className={INP} />
            </Field>
          </div>

          {section === "weekdays" && <WeekdaysSection rule={rule} update={update} toggleWeekday={toggleWeekday} setActiveWeekday={setActiveWeekday} />}
          {section === "pauses" && <PausesSection rule={rule} update={update} />}
          {section === "km" && <KMSection rule={rule} update={update} />}
          {section === "caps" && <CapsSection rule={rule} update={update} />}
          {section === "overtime" && <OvertimeSection rule={rule} update={update} />}
          {section === "preview" && <PreviewSection rule={rule} />}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0">
          <button onClick={onCancel} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
            Abbrechen
          </button>
          <button onClick={onSave} data-testid="autogen-rule-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Save className="w-4 h-4" /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-sections
// ============================================================================
function WeekdaysSection({ rule, update, toggleWeekday, setActiveWeekday }) {
  const exclude = new Set(rule.exclude_weekdays || []);
  return (
    <div className="space-y-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Wochenend-Ausschluss</div>
        <div className="flex gap-1">
          {EXCLUDE_WD_LABELS.map((wd, i) => (
            <button key={i} onClick={() => toggleWeekday(i)}
              data-testid={`autogen-exclude-${i}`}
              className={`px-3 py-1.5 text-xs rounded font-medium ${exclude.has(i)
                ? "bg-red-500/20 text-red-300 border border-red-500/30"
                : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"}`}>
              {wd} {exclude.has(i) ? "✕" : "✓"}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-zinc-500 mt-2">
          Tage in Rot werden vom Generator übersprungen. Klick zum Umschalten.
        </p>
      </div>

      {WEEKDAYS.map(({ key, label }) => {
        const wr = rule.weekday_rules?.[key];
        const isActive = !!wr;
        return (
          <div key={key} className="bg-zinc-950 border border-zinc-800 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-zinc-200">{label}</div>
              <label className="inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={e => setActiveWeekday(key, e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-blue-500" />
                Regel definiert
              </label>
            </div>
            {isActive ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <Field label="Start (min)">
                  <input type="time" value={wr.start[0]}
                    onChange={e => update(`weekday_rules.${key}.start`, [e.target.value, wr.start[1]])}
                    className={INP_SM} />
                </Field>
                <Field label="Start (max)">
                  <input type="time" value={wr.start[1]}
                    onChange={e => update(`weekday_rules.${key}.start`, [wr.start[0], e.target.value])}
                    className={INP_SM} />
                </Field>
                <Field label="Dauer min (Min)">
                  <input type="number" value={wr.duration_min}
                    onChange={e => update(`weekday_rules.${key}.duration_min`, parseInt(e.target.value || "0", 10))}
                    className={INP_SM} />
                </Field>
                <Field label="Dauer max (Min)">
                  <input type="number" value={wr.duration_max}
                    onChange={e => update(`weekday_rules.${key}.duration_max`, parseInt(e.target.value || "0", 10))}
                    className={INP_SM} />
                </Field>
                <Field label="Ende-Fenster">
                  <div className="flex gap-1 items-center">
                    <input type="time" value={(wr.end || ["", ""])[0] || ""}
                      onChange={e => update(`weekday_rules.${key}.end`, e.target.value ? [e.target.value, (wr.end || ["", ""])[1] || e.target.value] : null)}
                      className={INP_SM + " w-full"} />
                    <span className="text-zinc-500">–</span>
                    <input type="time" value={(wr.end || ["", ""])[1] || ""}
                      onChange={e => update(`weekday_rules.${key}.end`, e.target.value ? [(wr.end || ["", ""])[0] || e.target.value, e.target.value] : null)}
                      className={INP_SM + " w-full"} />
                  </div>
                </Field>
              </div>
            ) : (
              <p className="text-[11px] text-zinc-500 italic">— Tag wird übersprungen —</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PausesSection({ rule, update }) {
  return (
    <div className="space-y-4">
      <SectionCard title="SO-Pausen (Sonstige Arbeitszeit)">
        <Range2 label="SO1 — Offset nach Start (Min)" v={rule.pause_so.offset_min_after_start}
          on={v => update("pause_so.offset_min_after_start", v)} />
        <Range2 label="SO2 — Offset nach SO1 (Min)" v={rule.pause_so.offset_min_after_prev}
          on={v => update("pause_so.offset_min_after_prev", v)} />
        <Range2 label="SO2 — Dauer (Min)" v={rule.pause_so.so2_duration}
          on={v => update("pause_so.so2_duration", v)} />
        <Range2 label="SO3 — vor Ende (Min)" v={rule.pause_so.so3_before_end}
          on={v => update("pause_so.so3_before_end", v)} />
      </SectionCard>
      <SectionCard title="RP — Ruhepause">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Fenster Start">
            <input type="time" value={rule.pause_rp.window[0]}
              onChange={e => update("pause_rp.window", [e.target.value, rule.pause_rp.window[1]])}
              className={INP_SM} />
          </Field>
          <Field label="Fenster Ende">
            <input type="time" value={rule.pause_rp.window[1]}
              onChange={e => update("pause_rp.window", [rule.pause_rp.window[0], e.target.value])}
              className={INP_SM} />
          </Field>
          <Range2 label="Standard-Dauer (Min)" v={rule.pause_rp.duration}
            on={v => update("pause_rp.duration", v)} />
          <Field label="Verlängerte Dauer (Min) bei Überstunden">
            <input type="number" value={rule.pause_rp.extended_duration}
              onChange={e => update("pause_rp.extended_duration", parseInt(e.target.value || "0", 10))}
              className={INP_SM} />
          </Field>
        </div>
      </SectionCard>
    </div>
  );
}

function KMSection({ rule, update }) {
  return (
    <SectionCard title="KM-Logik">
      <Range2 label="Tageskilometer-Range (km)" v={rule.km.daily_range}
        on={v => update("km.daily_range", v)} />
      <Field label="Fallback-Start-KM (wenn weder Mitarbeiter noch Fahrzeug KM hinterlegt haben)">
        <input type="number" value={rule.km.fallback_start}
          onChange={e => update("km.fallback_start", parseInt(e.target.value || "0", 10))}
          className={INP_SM} />
      </Field>
      <p className="text-[11px] text-zinc-500 italic">
        Cascade beim Start-KM: 1) Letzter End-KM des Mitarbeiters · 2) Aktueller KM-Stand des Fahrzeugs · 3) Dieser Fallback.
      </p>
    </SectionCard>
  );
}

function CapsSection({ rule, update }) {
  return (
    <SectionCard title="Hard-Caps">
      <Field label="Spätester Arbeitsende-Zeitpunkt">
        <input type="time" value={rule.hard_caps.latest_end}
          onChange={e => update("hard_caps.latest_end", e.target.value)}
          className={INP_SM} />
      </Field>
      <Field label="Max. Arbeitszeit-Spanne pro Tag (Min)">
        <input type="number" value={rule.hard_caps.max_span_min}
          onChange={e => update("hard_caps.max_span_min", parseInt(e.target.value || "0", 10))}
          className={INP_SM} />
      </Field>
      <p className="text-[11px] text-zinc-500 italic">
        Niemals werden Arbeits- oder Lenkende über diesen Wert hinaus generiert — auch nicht durch Überstunden.
      </p>
    </SectionCard>
  );
}

function OvertimeSection({ rule, update }) {
  return (
    <SectionCard title="Überstunden-Verteilung">
      <Field label="Schritt-Größe (Min)">
        <input type="number" value={rule.overtime.step}
          onChange={e => update("overtime.step", parseInt(e.target.value || "0", 10))}
          className={INP_SM} />
        <p className="text-[10px] text-zinc-500 mt-0.5">Überstunden werden in Vielfachen dieses Schritts verteilt (typisch 15 Min).</p>
      </Field>
      <Field label="Max. Überstunden pro Tag (Min)">
        <input type="number" value={rule.overtime.max_per_day}
          onChange={e => update("overtime.max_per_day", parseInt(e.target.value || "0", 10))}
          className={INP_SM} />
      </Field>
      <Field label="RP-Verlängerung ab (Min Überstunden)">
        <input type="number" value={rule.overtime.rp_extend_threshold}
          onChange={e => update("overtime.rp_extend_threshold", parseInt(e.target.value || "0", 10))}
          className={INP_SM} />
        <p className="text-[10px] text-zinc-500 mt-0.5">
          Sobald an einem Tag mehr als dieser Wert an Überstunden verteilt wurde, wird die RP automatisch auf die verlängerte Dauer gesetzt.
        </p>
      </Field>
    </SectionCard>
  );
}

function PreviewSection({ rule }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  async function runPreview() {
    setBusy(true);
    try {
      // Save first, then run a real preview for tomorrow's date as a single-day test
      const { data: saved } = await api.post("/auto-gen/rule-groups", rule);
      const emp = (await api.get("/employees")).data.find(e => e.role === "fahrer");
      if (!emp) throw new Error("Kein Fahrer im System für Vorschau");
      const today = new Date();
      const { data } = await api.post("/auto-gen/preview", {
        employee_id: emp.id,
        vehicle_id: "",
        rule_group_id: saved.id,
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        overtime_target_hours: 0,
        include_saturdays: false,
      });
      // Show first non-skipped working day
      const day = (data.draft?.days || []).find(d => !d.skipped);
      setResult({ day, summary: data.summary, employee: emp });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Vorschau fehlgeschlagen");
    } finally { setBusy(false); }
  }
  return (
    <div className="space-y-4">
      <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 flex items-start gap-3">
        <Eye className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-blue-200">
          Vorschau zeigt einen Beispieltag mit den aktuell eingegebenen Werten.
          Speichert die Regel automatisch davor.
        </div>
      </div>
      <button onClick={runPreview} disabled={busy}
        data-testid="autogen-rule-preview-run"
        className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded font-medium disabled:opacity-50">
        <Eye className="w-4 h-4" /> {busy ? "Berechne …" : "Vorschau ausführen"}
      </button>
      {result?.day && (
        <div className="bg-zinc-950 border border-zinc-800 rounded p-4 text-xs space-y-2 font-mono">
          <div className="text-amber-300 font-semibold mb-2 text-sm">
            Beispiel-Tag: {result.day.date} ({["Mo","Di","Mi","Do","Fr","Sa","So"][result.day.weekday]})
          </div>
          <Row label="Arbeitszeit" value={`${result.day.work_start} – ${result.day.work_end}`} />
          <Row label="Lenkzeit" value={`${result.day.driving_start} – ${result.day.driving_end}`} />
          <Row label="Start-KM → End-KM" value={`${result.day.start_km?.toLocaleString("de-AT")} → ${result.day.end_km?.toLocaleString("de-AT")}`} />
          <div className="pt-2 border-t border-zinc-800 mt-2">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Pausen</div>
            {(result.day.pauses || []).map(p => (
              <div key={p.label} className="text-zinc-300">
                <span className="text-zinc-500 w-12 inline-block">{p.label}</span> {p.start} – {p.end}
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-zinc-800 mt-2 text-zinc-400 text-[11px]">
            Monatsstatistik: {result.summary.days_generated} Tage generiert,
            {" "}{result.summary.days_skipped} geskipt, {result.summary.days_with_existing_entry} überschrieben
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tiny helpers
// ============================================================================
const INP = "w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-sm text-zinc-100 focus:border-blue-500/60 outline-none";
const INP_SM = "w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 font-mono focus:border-blue-500/60 outline-none";

function Field({ label, children, colSpan = 1 }) {
  const cls = colSpan === 3 ? "md:col-span-3" : colSpan === 2 ? "md:col-span-2" : "";
  return (
    <div className={cls}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
      <div className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-400" /> {title}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Range2({ label, v, on }) {
  return (
    <Field label={label}>
      <div className="flex gap-1 items-center">
        <input type="number" value={v[0]} onChange={e => on([parseInt(e.target.value || "0", 10), v[1]])} className={INP_SM} />
        <span className="text-zinc-500">–</span>
        <input type="number" value={v[1]} onChange={e => on([v[0], parseInt(e.target.value || "0", 10)])} className={INP_SM} />
      </div>
    </Field>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex">
      <span className="text-zinc-500 w-44 flex-shrink-0">{label}</span>
      <span className="text-zinc-100">{value}</span>
    </div>
  );
}
